import { inject, Service } from '@angular/core';
import { GitBranchCompareModel } from '@common/git/models/git-branch-compare.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { Branch } from '@portal-core/branches/models/branch.model';
import { BranchesApiService } from '@portal-core/branches/services/branches-api.service';
import { BranchesDataService } from '@portal-core/branches/services/branches-data.service';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { GetDataOptions } from '@portal-core/data/common/models/get-data-options.model';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { map, Observable, of, switchMap, tap } from 'rxjs';

@Service()
@Resettable()
export class BranchesService extends CollectionServiceBase<Branch> {
  private readonly branchesApiService = inject(BranchesApiService);

  constructor() {
    super(inject(BranchesDataService));
  }

  protected override fetchItemById$(branchId: string): Observable<Branch> {
    return this.branchesApiService.getBranchFromProjectId$(this.getProjectIdFromId(branchId), this.getBranchNameFromId(branchId)).pipe(
      map(branch => this.processBranch(branch))
    );
  }

  protected override fetchItemsById$(itemIds: string[]): Observable<Branch[]> {
    // TODO: use a new bulk load endpoint and call processBranches
    // THEN: test
    // create-build-dialog
    // build-picker in site-settings-form
    return of(itemIds.map(itemId => this.createBranchFromId(itemId)));
  }

  getProjectBranchesPageByProjectId$(projectId: number, filter: PageFilter): Observable<Page<Branch>> {
    return this.branchesApiService.getProjectBranchesPageByProjectId$(projectId, filter).pipe(
      tap(page => this.processBranches(page.Items))
    );
  }

  getBuildBranchesPageByProjectId$(projectId: number, filter: PageFilter): Observable<Page<Branch>> {
    return this.branchesApiService.getBuildBranchesPageByProjectId$(projectId, filter).pipe(
      tap(page => this.processBranches(page.Items))
    );
  }

  getBranchFromProjectId$(projectId: number, branchName: string, options: GetDataOptions = null): Observable<Branch> {
    return this.getItemById$(this.buildBranchId(projectId, branchName), options);
  }

  getProjectBranches$(projectId: number): Observable<string[]> {
    return this.branchesApiService.getProjectBranches$(projectId);
  }

  getDefaultBranch$(projectId: number): Observable<string> {
    return this.branchesApiService.getDefaultBranch$(projectId).pipe(
      tap(defaultBranch => {

        if (defaultBranch) {
          const branch = {
            Id: this.buildBranchId(projectId, defaultBranch.Name),
            Name: defaultBranch.Name,
            ProjectId: projectId,
            Commit: defaultBranch.Commit
          };
          this.addItems$([branch]);
        }
      }),
      map(branch => branch?.Name)
    );
  }

  buildBranchId(projectId: number, branchName: string): string {
    if (typeof projectId === 'number' && typeof branchName === 'string') {
      return `${projectId}-${branchName}`;
    }
  }

  getProjectIdFromId(branchId: string): number {
    if (typeof branchId === 'string') {
      const index = branchId.indexOf('-');
      if (index >= 0) {
        return parseInt(branchId.substring(0, index), 10);
      }
    }
  }

  getBranchNameFromId(branchId: string): string {
    if (typeof branchId === 'string') {
      const index = branchId.indexOf('-');
      if (index >= 0) {
        return branchId.substring(index + 1);
      }
    }
  }

  createBranchFromId(branchId: string): Branch {
    if (typeof branchId === 'string') {
      const index = branchId.indexOf('-');
      if (index >= 0) {
        return {
          Id: branchId,
          ProjectId: parseInt(branchId.substring(0, index), 10),
          Name: branchId.substring(index + 1)
        };
      }
    }
  }

  private processBranches(branches: Branch[]) {
    if (branches?.length > 0) {
      branches.forEach(branch => this.processBranch(branch));
    }
    return branches;
  }

  private processBranch(branch: Branch): Branch {
    if (!branch.Id) {
      branch.Id = this.buildBranchId(branch.ProjectId, branch.Name);
    }
    return branch;
  }

  createBranch$(projectId: number, sourceBranch: string, name: string): Observable<Branch> {
    return this.branchesApiService.createBranch$(projectId, sourceBranch, name).pipe(
      // Wait for data service to finish
      switchMap(branch => this.addItems$([branch]).pipe(
        map(() => branch)
      ))
    );
  }

  deleteBranches$(projectId: number, branches: Branch[]): Observable<void> {
    const branchNames = branches.map(branch => branch.Name);
    return this.branchesApiService.deleteBranches$(projectId, branchNames).pipe(
      switchMap(() => this.deleteItems$(branches))
    );
  }

  compareBranches$(projectId: number, fromBranchName: string, toBranchName: string): Observable<GitBranchCompareModel> {
    return this.branchesApiService.compareBranches$(projectId, fromBranchName, toBranchName);
  }
}
